import {
  ADVANCED_ONLY_FIELD_TYPES,
  isDerived,
  renderFieldValue,
  type FieldDefinition,
  type FieldValue,
  type RenderedValue,
} from './fields.js';
import { DEFAULT_FORMAT_CONTEXT, absent, type FormatContext } from './format.js';

/**
 * The render planner.
 *
 * A pure function of (layout, record, mode, permissions). No React, no DOM, no
 * clock, no fetch - it takes everything it needs as an argument and returns a
 * plain object. `layout.purity.test.ts` reads this file's own source and fails
 * if an import or a global sneaks in, because "this is pure" is the sort of
 * claim that stays true right up until somebody needs one small thing.
 *
 * Purity is not tidiness here. It is what lets the hard part - which fields
 * appear, in which order, under which budget, with which of them redacted -
 * be tested exhaustively without rendering anything, and lets the renderer
 * below it be a dumb translation from plan to elements.
 */

export const FIELD_BUDGET_SIMPLE = 8;

export type LayoutMode = 'simple' | 'advanced';

export interface LayoutSection {
  key: string;
  label: string;
  columns: 1 | 2;
  fields: string[];
  /** Sections a workspace marked as advanced-only, whatever their fields. */
  advancedOnly?: boolean;
}

export interface LayoutSpec {
  sections: LayoutSection[];
}

/**
 * What the caller may do with each field.
 *
 * Absent from the map means the field is not mentioned by any permission set,
 * which is not the same as being denied - it is the default, and the default
 * is readable. A field the caller may not see must be present and say so, so
 * that "I was not told about it" and "I am not allowed" cannot collapse into
 * the same silence (P-5).
 */
export interface FieldAccess {
  readable: boolean;
  editable: boolean;
}

/** Why a field the layout named is not on the screen. */
export type OmissionReason =
  /** Field-level security. The person is told a field exists and is hidden. */
  | 'field-permission'
  /** Simple mode. One switch reveals it, and the screen says so. */
  | 'advanced-only'
  /** Simple mode's eight-field budget. */
  | 'over-budget'
  /** The layout names a field the object does not define. */
  | 'no-such-field';

export interface PlannedField {
  key: string;
  label: string;
  type: FieldDefinition['type'];
  required: boolean;
  helpText: string | null;
  /** Null when the field is present but the caller may not edit it. */
  editable: boolean;
  /** Derived fields are never editable by anybody, which is a different fact. */
  derived: boolean;
  value: FieldValue;
  rendered: RenderedValue;
  definition: FieldDefinition;
}

export interface OmittedField {
  key: string;
  /** The label is shown even when the value is not, for `field-permission`. */
  label: string;
  reason: OmissionReason;
}

export interface PlannedSection {
  key: string;
  label: string;
  columns: 1 | 2;
  fields: PlannedField[];
  omitted: OmittedField[];
}

export interface RenderPlan {
  mode: LayoutMode;
  sections: PlannedSection[];
  /** Every omission across every section, so a screen can say one sentence. */
  omitted: OmittedField[];
  budget: {
    limit: number | null;
    shown: number;
    /** Hidden by the budget alone, not by permission or by mode. */
    overBudget: number;
  };
  /** True when switching to Advanced would reveal something. */
  moreInAdvanced: boolean;
}

export interface PlanInput {
  layout: LayoutSpec;
  fields: readonly FieldDefinition[];
  record: Readonly<Record<string, FieldValue>>;
  mode: LayoutMode;
  /** Per-field access. A missing entry means readable and editable. */
  access?: Readonly<Record<string, FieldAccess>>;
  /** False for a record the caller may read but not change. */
  canEditRecord?: boolean;
  ctx?: FormatContext;
  /** Overridable so a test can prove the budget is the budget. */
  budget?: number | null;
}

const DEFAULT_ACCESS: FieldAccess = { readable: true, editable: true };

/**
 * Which fields appear, in what order, and what the reader is told about the
 * ones that do not.
 *
 * The budget applies to the whole record, not per section: eight fields across
 * three sections is eight fields to read. Fields are dropped from the end of
 * the layout, because the layout's order is the workspace's statement of what
 * matters, and a budget that dropped from the middle would make that statement
 * unreadable.
 */
export function planLayout(input: PlanInput): RenderPlan {
  const ctx = input.ctx ?? DEFAULT_FORMAT_CONTEXT;
  const access = input.access ?? {};
  const canEditRecord = input.canEditRecord !== false;
  const byKey = new Map(input.fields.map((f) => [f.key, f]));
  const limit =
    input.budget !== undefined ? input.budget : input.mode === 'simple' ? FIELD_BUDGET_SIMPLE : null;

  const sections: PlannedSection[] = [];
  const omitted: OmittedField[] = [];
  let shown = 0;
  let overBudget = 0;
  let moreInAdvanced = false;

  for (const section of input.layout.sections) {
    const planned: PlannedField[] = [];
    const sectionOmitted: OmittedField[] = [];

    const sectionIsAdvanced = section.advancedOnly === true && input.mode === 'simple';

    for (const key of section.fields) {
      const definition = byKey.get(key);
      if (!definition) {
        // The layout and the field list are two answers to one question, and
        // they can disagree: a field is deleted and a layout still names it.
        // Saying so beats rendering a gap where a field used to be (P-1).
        const entry: OmittedField = { key, label: key, reason: 'no-such-field' };
        sectionOmitted.push(entry);
        omitted.push(entry);
        continue;
      }

      const fieldAccess = access[key] ?? DEFAULT_ACCESS;
      if (!fieldAccess.readable) {
        const entry: OmittedField = { key, label: definition.label, reason: 'field-permission' };
        sectionOmitted.push(entry);
        omitted.push(entry);
        continue;
      }

      const advancedOnly =
        sectionIsAdvanced || (input.mode === 'simple' && ADVANCED_ONLY_FIELD_TYPES.includes(definition.type));
      if (advancedOnly) {
        const entry: OmittedField = { key, label: definition.label, reason: 'advanced-only' };
        sectionOmitted.push(entry);
        omitted.push(entry);
        moreInAdvanced = true;
        continue;
      }

      if (limit !== null && shown >= limit) {
        const entry: OmittedField = { key, label: definition.label, reason: 'over-budget' };
        sectionOmitted.push(entry);
        omitted.push(entry);
        overBudget += 1;
        moreInAdvanced = true;
        continue;
      }

      const value = input.record[key] ?? null;
      const derived = isDerived(definition.type);
      planned.push({
        key,
        label: definition.label,
        type: definition.type,
        required: definition.required,
        helpText: definition.helpText ?? null,
        derived,
        editable: canEditRecord && fieldAccess.editable && !derived,
        value,
        rendered: renderFieldValue(definition, value, input.record, ctx),
        definition,
      });
      shown += 1;
    }

    // A section with nothing left in it still reports what it lost, so a
    // screen can say "three fields hidden" rather than showing a bare heading.
    sections.push({
      key: section.key,
      label: section.label,
      columns: section.columns,
      fields: planned,
      omitted: sectionOmitted,
    });
  }

  return {
    mode: input.mode,
    sections,
    omitted,
    budget: { limit, shown, overBudget },
    moreInAdvanced,
  };
}

/**
 * What to render where a hidden field's value would have been.
 *
 * Field-level security says the field exists and its value is not for you.
 * Rendering nothing at all would say the field does not exist, and those are
 * different facts about the record (P-5).
 */
export function hiddenFieldValue(ctx: FormatContext = DEFAULT_FORMAT_CONTEXT): RenderedValue {
  return { kind: 'absent', absent: absent('hidden', ctx) };
}

/**
 * A one-line summary of what the reader is not being shown.
 *
 * Returned as counts by reason rather than a sentence, because the sentence
 * has to be translated and this file does not know the language.
 */
export function omissionSummary(plan: RenderPlan): Record<OmissionReason, number> {
  const summary: Record<OmissionReason, number> = {
    'field-permission': 0,
    'advanced-only': 0,
    'over-budget': 0,
    'no-such-field': 0,
  };
  for (const entry of plan.omitted) summary[entry.reason] += 1;
  return summary;
}

/**
 * How many columns a list shows in Simple.
 *
 * Fewer than the record's eight, because a column costs horizontal room that a
 * field does not: the failure a narrow list produces is "Comp..." / "Creat..."
 * truncated headers, which is worse than a column nobody asked for being
 * absent. Four is what fits at 1280 with the row selector and the first column
 * sticky.
 */
export const COLUMN_BUDGET_SIMPLE = 4;

/** The shape this needs from a column. Anything wider satisfies it. */
export interface BudgetedColumn {
  key: string;
  /** Never dropped, whatever the budget: the column the row is identified by. */
  essential?: boolean;
  /** Shown only in Advanced, whatever the budget. */
  advancedOnly?: boolean;
}

export interface ColumnPlan<C> {
  columns: C[];
  /** How many the mode removed, so a list can say so rather than just be short. */
  hidden: number;
}

/**
 * Which columns a list shows in a given mode.
 *
 * Pure, and deliberately the same shape as the field planner: one rule about
 * what Simple means, applied in both places, rather than each list screen
 * curating its own set. A per-screen list is a list that drifts, and the drift
 * shows up as one list obeying the preference and the next ignoring it.
 *
 * Essential columns are kept even past the budget. A list whose first column
 * was budgeted away is a list of rows you cannot identify, which is not a
 * simpler list - it is a broken one.
 */
export function planColumns<C extends BudgetedColumn>(
  columns: readonly C[],
  mode: LayoutMode,
  budget: number = COLUMN_BUDGET_SIMPLE,
): ColumnPlan<C> {
  if (mode === 'advanced') return { columns: [...columns], hidden: 0 };

  const kept: C[] = [];
  for (const column of columns) {
    // Essential is asked FIRST, so a column marked both essential and
    // advanced-only survives. The markings contradict each other and the safe
    // reading is to show it: a list missing the column its rows are identified
    // by is a worse failure than a list showing one column too many.
    if (column.essential) {
      kept.push(column);
      continue;
    }
    if (column.advancedOnly) continue;
    if (kept.length < budget) kept.push(column);
  }
  return { columns: kept, hidden: columns.length - kept.length };
}
